/* eslint-disable @typescript-eslint/no-non-null-assertion */
import * as React from 'react';
import { Map as FullwidthMap, IMapGeoObject, IRouteData } from '@cian/frontend-newbuilding-map-component';
import { UIText3 } from '@cian/ui-kit';
import classNames from 'clsx';

import { ICoordinates } from '../../types/geo';
import { SectionTitle } from '../SectionTitle';
import { IRoutingBuildSuccessParams } from './types';
import { useMapFirstPaintPartialOptions, useMapMedia } from './hooks';
import * as styles from './Map.css';

export interface IMapProps {
  apiKey: string;
  apiVersion?: string;
  currentTypes: string[];
  infrastructureTypes?: React.ReactNode;
  isFullscreen?: boolean;
  mapRef?: React.Ref<FullwidthMap>;
  mapServices?: React.ReactNode;
  onActiveRouteChange?(route: IRouteData | null): void;
  onBalloonHtmlFirstFetch?: (item: IMapGeoObject) => Promise<string>;
  onBalloonOpen?: (item: IMapGeoObject) => void;
  onError?: (error: Error) => void;
  onGeoObjectsFirstFetch: () => Promise<IMapGeoObject[]>;
  onFullscreenEnter?(): void;
  onFullscreenExit?(): void;
  onMapLoaded?(): void;
  onNoPanoramaEnter?(): void;
  onPanoramaExit?(): void;
  onPanoramaSuccessEnter?(): void;
  onRouteBuildingInit?(): void;
  onRouteBuildingSuccess?(params: IRoutingBuildSuccessParams): void;
  onRoutingStart?(): void;
  onRoutingEnd?(): void;
  onRulerClick?(): void;
  pinCoordinates: ICoordinates;
  routeButton?: React.ReactNode;
  transportAccessibilityService?: React.ReactNode;
  warningText?: string;

  // TODO: Одно из свойств будет удалено в NB-1172 при выпиле фича-флага
  years?: React.ReactNode;
  yearsService?: React.ReactNode;

  zoom: number;
}

function Component({
  apiKey,
  apiVersion,
  currentTypes,
  infrastructureTypes,
  isFullscreen,
  mapRef,
  mapServices,
  onActiveRouteChange,
  onBalloonHtmlFirstFetch,
  onBalloonOpen,
  onFullscreenEnter,
  onFullscreenExit,
  onError,
  onGeoObjectsFirstFetch,
  onMapLoaded,
  onNoPanoramaEnter,
  onPanoramaExit,
  onPanoramaSuccessEnter,
  onRouteBuildingInit,
  onRouteBuildingSuccess,
  onRoutingStart,
  onRoutingEnd,
  onRulerClick,
  pinCoordinates,
  routeButton,
  transportAccessibilityService,
  warningText,
  years,
  yearsService,
  zoom,
}: IMapProps) {
  const media = useMapMedia(pinCoordinates, zoom);
  const firstPaintOptions = useMapFirstPaintPartialOptions(onRulerClick);

  return (
    <div className={styles['wrapper']}>
      <div className={classNames(styles['header'], warningText && styles['noMargin'])}>
        <SectionTitle>Инфраструктура</SectionTitle>
      </div>
      {warningText && (
        <div className={styles['warningText']}>
          <UIText3 color="gray60_100">{warningText}</UIText3>
        </div>
      )}
      <div className={classNames(styles['container'], isFullscreen && styles['fullscreen-mode'])}>
        <FullwidthMap
          ref={mapRef}
          geoObjectNames={currentTypes}
          media={media}
          firstPaintOptions={{
            ...firstPaintOptions,
            fetch: {
              onGeoObjectsFetch: onGeoObjectsFirstFetch,
              onBalloonHtmlFetch: onBalloonHtmlFirstFetch,
            },
            apiKey,
            apiVersion,
            onActiveRouteChange,
            onBalloonOpen,
            onError,
            onFullscreenEnter,
            onFullscreenExit,
            onMapSuccess: onMapLoaded,
            onNoPanoramaEnter,
            onPanoramaExit,
            onPanoramaSuccessEnter,
            onRouteBuildingInit,
            onRouteBuildingSuccess,
            onRoutingEnd,
            onRoutingStart,
          }}
        />
        {routeButton && <div className={styles['route-button']}>{routeButton}</div>}
        {infrastructureTypes && <div className={styles['infrastructure']}>{infrastructureTypes}</div>}
        {years && <div className={styles['years']}>{years}</div>}
        {mapServices && <div className={styles['map-services']}>{mapServices}</div>}
        {transportAccessibilityService && (
          <div className={styles['transport-accessibility']}>{transportAccessibilityService}</div>
        )}
        {yearsService && <div className={styles['years-service']}>{yearsService}</div>}
      </div>
    </div>
  );
}

Component.displayName = 'Map';

export const Map = Component;
